import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatIDR } from "./format";

const NAVY = [10, 17, 40];
const GOLD = [212, 175, 55];

export function generatePayslipPDF({ slip, run, company, preview }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // Header
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, 210, 32, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 32, 210, 1.5, "F");
  if (company?.logo_base64) {
    try { doc.addImage(company.logo_base64, "PNG", 14, 6, 20, 20); } catch (e) {}
  }
  const brandX = company?.logo_base64 ? 38 : 14;
  const brandW = 196 - brandX - 35;
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text((company?.company_name || "FUJI INDO SURYA ERP SYSTEM").toUpperCase(), brandX, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  let hy = 17;
  if (company?.address) {
    const addrLines = doc.splitTextToSize(company.address, brandW);
    doc.text(addrLines.slice(0, 2), brandX, hy);
    hy += Math.min(addrLines.length, 2) * 3.2;
  }
  if (company?.tax_id) doc.text(`NPWP: ${company.tax_id}`, brandX, hy);

  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("SLIP GAJI", 196, 16, { align: "right" });
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text(`Periode: ${run.period}`, 196, 22, { align: "right" });

  // Employee info
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  let y = 44;
  const label = (t, v, x1 = 14, x2 = 55) => {
    doc.setTextColor(100, 116, 139);
    doc.text(t, x1, y);
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.text(String(v || "-"), x2, y);
    doc.setFont("helvetica", "normal");
  };
  label("No. Karyawan", slip.employee_code); label("Nama", slip.employee_name, 110, 140); y += 6;
  label("Departemen", slip.department); label("Jabatan", slip.position, 110, 140); y += 6;
  label("Status PTKP", slip.marital_status); label("No. Slip", run.run_number, 110, 140); y += 8;

  // Earnings table
  const earnings = [
    ["Gaji Pokok", formatIDR(slip.basic_salary)],
    ...slip.allowances.map((a) => [`Tunjangan ${a.name}${a.taxable ? "" : " (non-tax)"}`, formatIDR(a.amount)]),
  ];
  autoTable(doc, {
    startY: y,
    head: [["PENGHASILAN", "JUMLAH"]],
    body: earnings,
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: [30, 41, 59] },
    alternateRowStyles: { fillColor: [240, 253, 244] },
    columnStyles: { 1: { halign: "right", cellWidth: 45, textColor: [16, 185, 129], fontStyle: "bold" } },
    margin: { left: 14, right: 110 },
    tableWidth: 86,
  });
  const eEnd = doc.lastAutoTable.finalY;

  // Deductions table
  const bp = slip.bpjs;
  const deductions = [
    ["BPJS Kesehatan (1%)", formatIDR(bp.kes_employee)],
    ["BPJS JHT (2%)", formatIDR(bp.jht_employee)],
    ["BPJS JP (1%)", formatIDR(bp.jp_employee)],
    [`PPh 21 (TER ${(slip.pph21.rate * 100).toFixed(2)}%)`, formatIDR(slip.pph21.amount)],
    ...slip.additional_deductions.map((d) => [d.name, formatIDR(d.amount)]),
  ];
  autoTable(doc, {
    startY: 44 + 6 + 6 + 8 - 1, // align with earnings top
    head: [["POTONGAN", "JUMLAH"]],
    body: deductions,
    headStyles: { fillColor: [153, 27, 27], textColor: [255, 255, 255], fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: [30, 41, 59] },
    alternateRowStyles: { fillColor: [254, 242, 242] },
    columnStyles: { 1: { halign: "right", cellWidth: 45, textColor: [220, 38, 38], fontStyle: "bold" } },
    margin: { left: 110, right: 14 },
    tableWidth: 86,
  });
  const dEnd = doc.lastAutoTable.finalY;

  const summaryY = Math.max(eEnd, dEnd) + 8;
  autoTable(doc, {
    startY: summaryY,
    head: [["RINGKASAN", "JUMLAH"]],
    body: [
      [{ content: "Total Penghasilan (Earnings)", styles: { fillColor: [240, 253, 244], textColor: NAVY } },
       { content: formatIDR(slip.total_earnings), styles: { fillColor: [240, 253, 244], textColor: [16, 185, 129], halign: "right", fontStyle: "bold" } }],
      [{ content: "Total Potongan (Deductions)", styles: { fillColor: [254, 242, 242], textColor: NAVY } },
       { content: `- ${formatIDR(slip.total_deductions)}`, styles: { fillColor: [254, 242, 242], textColor: [220, 38, 38], halign: "right", fontStyle: "bold" } }],
      [{ content: "TAKE HOME PAY (Gaji Bersih)", styles: { fontStyle: "bold", fillColor: GOLD, textColor: NAVY, fontSize: 11 } },
       { content: formatIDR(slip.take_home), styles: { fontStyle: "bold", fillColor: GOLD, textColor: NAVY, halign: "right", fontSize: 12 } }],
    ],
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 10 },
    bodyStyles: { fontSize: 10 },
    columnStyles: { 1: { halign: "right" } },
    margin: { left: 14, right: 14 },
  });

  // Company contribution info (informational)
  const cEnd = doc.lastAutoTable.finalY + 6;
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text("Kontribusi Perusahaan (bukan potongan karyawan):", 14, cEnd);
  doc.text(
    `BPJS Kes ${formatIDR(bp.kes_company)} · JHT ${formatIDR(bp.jht_company)} · JKK ${formatIDR(bp.jkk_company)} · JKM ${formatIDR(bp.jkm_company)} · JP ${formatIDR(bp.jp_company)}`,
    14, cEnd + 4, { maxWidth: 182 }
  );

  // Signature
  const sigY = 260;
  doc.setDrawColor(200, 200, 200);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(15, 23, 42);
  const boxes = [
    { x: 25, w: 60, label: "Diketahui oleh", sub: "(HR / Finance)" },
    { x: 125, w: 60, label: "Diterima oleh", sub: "(Karyawan)" },
  ];
  boxes.forEach((b) => {
    doc.text(b.label, b.x + b.w / 2, sigY, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(b.sub, b.x + b.w / 2, sigY + 4, { align: "center" });
    doc.line(b.x, sigY + 22, b.x + b.w, sigY + 22);
    doc.setFontSize(8);
    doc.text("Nama & Tanda Tangan", b.x + b.w / 2, sigY + 27, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(9);
  });

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(148, 163, 184);
  doc.text(`Dokumen bersifat rahasia · Diterbitkan oleh ${company?.company_name || "FUJI INDO SURYA ERP SYSTEM"}`, 14, 289);

  const filename = `SlipGaji_${slip.employee_code}_${run.period}.pdf`;
  if (preview) return { url: doc.output("bloburl"), filename };
  doc.save(filename);
}
